/* eslint-disable no-console */
import React, { useCallback, useEffect, useState } from "react";
import GlobalContext from "@/context/GlobalContext";
import { Modals } from "@jd/focus-desktop-comps";
import {
  FocusSDK as focusSDK,
  getAppLogoConfig,
  isFocusEnv,
  getLoginURL,
  getWebEnvObj,
  forceLogin
} from "@/utils";
import i18n from "@/i18n";
import { duccConfigI18 } from "@/utils/i18n";
import { TFunction } from "i18next";
import { initReactI18next } from "react-i18next";
import resources from "@/i18n/resource";
import logger from "@/utils/logger";
import { message, Modal } from "antd";
import GError from "@/utils/GError";
import { BrowserRouter, HashRouter } from "react-router-dom";
import AppRuntimeEnv from "@/types/AppRuntimeEnv";
import Service from "@/services";
import ImService from "@/server/ImService";
import LoginPage from "@/pagesv2/LoginPage";
import Bus from "@/utils/bus";
import ConnectState from "@jd/jdee.im.sdk/lib/es/enum/ConnectState";
import { getServiceAndPrivacyLinks } from "@/utils/ui";
import UpgradeModal from "@/components/Modals/UpgradeModal";
import { setScanCodeInstance } from "@/server/scanCode";
import getVersionBySdk from "@/utils/getVersion";
import StartLoading from "./components/StartLoading";
import DownloadFileQueue from "./utils/DownloadFileQueue";
import NetworkCheck from "./utils/NetworkCheck";
import { getNetCheckUrl } from "./config/config";
import { debounce } from "lodash";
import { ChangeNetModalProps, NetType } from "./components/Modals/ChangeNetModal";
import InitJoySpaceController from "./components/Joyspace/controllers/InitJoySpaceController";
import SyncJoySpaceController from "./components/Joyspace/controllers/SyncJoySpaceController";
import { setJoySpaceDucc } from "./components/Joyspace/util";
import InitThirdAppService from "./components/ThirdApp/components/service/InitThirdAppService";
// import InitCardService from "./components/ThirdApp/components/service/InitCardService";
import NetTable from "./components/Modals/NetTable";
import BatchDownloadController from "./components/Joyspace/components/BatchDownloadPanel/BatchDownloadController";
import OfflineTip from "@/components/ErrorHolder/OfflineTip";
import AppContainer from "./AppContainer";
import GroupSpaceDownloadController from "./components/Joyspace/components/GroupDownloadPanel/BatchDownloadController";
import ImDownloadController from "./baseComponents/Chat/ImCollapsePanel/ImDownloadPanel/BatchDownloadController";
import {
  groupJoyspaceKey,
  GroupUploadControlCenterInstance
} from "./components/Joyspace/components/GroupUploadPanel/UploadController";
import sentryManual from "./utils/sentryManual";
import { RegistryConfigData } from "./types/LocalConfig";
import { closeUserSelectionModal } from "./utils/modals";
import { clearCookies } from "@/utils/localCache";

const { REACT_APP_BUILD_ELECTRON } = process.env;

const log = logger.getLogger("AppV2");

const localUrl = new URL(location.href);

window.onerror = (e: any) => {
  focusSDK.printLog("global error", JSON.stringify({ e }));
  console.error("global error", { e });
};

// 兼容IE
if (!document.body.scrollTo) {
  Element.prototype.scrollTo = function (option: any) {
    this.scrollLeft = option.left;
    this.scrollTop = option.top;
  };
}

const clearImServiceListeners = () => {
  const imService = ImService.getInstance();
  imService?.removeAllListeners("chat_new_message");
  imService?.removeAllListeners("chat_batch_message");
};

function App() {
  const pageName = localUrl.searchParams.get("page");
  const isHomePage = !pageName && !localUrl.pathname.includes("VideoViewer");
  const onGlobalError = useCallback(
    (cfg: any) => {
      let config = cfg;
      if (typeof cfg === "string") {
        config = {
          type: "message",
          error: GError.fromError(new Error(cfg))
        };
      } else if (cfg instanceof GError) {
        config = {
          type: "message",
          error: cfg
        };
      } else if (cfg instanceof Error) {
        config = {
          type: "message",
          error: GError.fromError(cfg)
        };
      }
      switch (config.type) {
        case "message": {
          let errorMsg = config.error.toString();
          if (pageName === "builtin-browser" && errorMsg.indexOf("Failed to fetch") > -1) {
            return;
          }
          message.error(errorMsg);
          return;
        }
        case "modal":
          Modal.error({
            content: config.error.toString()
          });
          return;
        default:
      }
    },
    [pageName]
  );
  const [service, setService] = useState<Service | null>(null);
  const [commonConfig, setCommonConfig] = useState<any>({});
  const [localConfigData, setLocalConfigData] = useState<RegistryConfigData | null>(null);
  const tRef = React.useRef<TFunction | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [changeNetProps, setChangeNetProps] = useState<ChangeNetModalProps>({
    isPrivateNet: focusSDK.isPrivateNet(),
    newPublicResult: false,
    newPrivateResult: false
  });
  const [netVisible, setNetVisible] = useState(false);
  const [currentAuthInfo, setCurrentAuthInfo] = useState(null);
  const [fromType, setFromType] = useState("");
  // const history = useHistory();
  const [currentNetOffline, setCurrentNetOffline] = useState(false);
  const [loginCurrentNetInfo, setLoginCurrentNetInfo] = useState(() => {
    if (focusSDK.getCurrentNetType() === NetType.MIX_NET) {
      return {
        loginPublicResult: true,
        loginPrivateResult: true
      };
    } else if (focusSDK.getCurrentNetType() === NetType.PRIVATE_NET) {
      return {
        loginPublicResult: false,
        loginPrivateResult: true
      };
    } else if (focusSDK.getCurrentNetType() === NetType.PUBLIC_NET) {
      return {
        loginPublicResult: true,
        loginPrivateResult: false
      };
    }
    return {
      loginPublicResult: true,
      loginPrivateResult: false
    };
  });

  const loadAppRuntimeEnv = useCallback((isReload = false, force?: boolean) => {
    const ts = Date.now();
    focusSDK.getAppRuntimeEnv((data) => {
      const { runtimeEnv, localConfig } = data;
      setLocalConfigData(localConfig);
      const s = new AppRuntimeEnv(runtimeEnv, localConfig);
      // ducc配置joyspace名称
      duccConfigI18.joyspace = s.getI18N("layout.nav.joyspace");
      document.title = s.productName;
      const service = new Service(s, focusSDK);
      // 屏蔽重要信息
      const joyspace: any = {
        ...service.appRuntime.envConfig.joyspace,
        aiConfigName: service.appRuntime.envConfig.aiConfig.aiUser
      };
      delete joyspace.host;
      delete joyspace.lowCode;
      delete joyspace.cookie;
      setJoySpaceDucc(joyspace);
      //   console.log("setService", service);
      setService(service);
      setScanCodeInstance(
        service.appRuntime.envConfig.webConfig,
        service.appRuntime.registryConfig
      );
      if (isReload) {
        focusSDK.emit("appContainerReload", service);
      }
    }, force);
  }, []);

  const onConfigDataChange = useCallback(() => {
    loadAppRuntimeEnv(false, true);
  }, []); //eslint-disable-line

  const onLoginSuccess = useCallback(() => {
    loadAppRuntimeEnv(true, true);
  }, []); //eslint-disable-line
  const claerJoyspaceService = useCallback(() => {
    try {
      InitJoySpaceController.clearServiceAll();
      SyncJoySpaceController.removeLineSyncData();

      // 销毁工作台数据库服务
      InitThirdAppService.clearServiceAll();
      // 销毁工作台卡片数据库服务
      // InitCardService.clearServiceAll();

      clearImServiceListeners();
      // 清空下载队列
      DownloadFileQueue.getInstance()?.clearQueue();
      // 清空文档下载队列
      BatchDownloadController.getInstance()?.clearTaskAll();
      // 清空组空间下载队列
      GroupSpaceDownloadController.getInstance()?.clearTaskAll();
      // 清空im下载队列
      ImDownloadController.getInstance()?.clearTaskAll();
      // 清空组空间上传队列
      const GroupUploadControl: any = GroupUploadControlCenterInstance.get(groupJoyspaceKey);
      if (GroupUploadControl) {
        GroupUploadControl?.clearAllUpload();
        GroupUploadControlCenterInstance?.clear(groupJoyspaceKey);
      }
    } catch (e) {}
  }, []);
  const onLogout = useCallback(
    async (reason?: string) => {
      // 清空当前用户信息
      setCurrentAuthInfo(null);

      // 清空cookie
      clearCookies();

      // 退出登录后 登出im
      if (isFocusEnv() && ImService.getInstance()) {
        ImService.getInstance().logoutIm(true);
      }
      claerJoyspaceService();
      if (!service) {
        return;
      }
      service.focusService.setToken("");
      if (reason !== "expired") {
        try {
          service.authService.logout().then(() => {
            if (service?.appRuntime?.registryConfig.auth) {
              delete service.appRuntime.registryConfig.auth;
              service.appRuntime.registryConfig.selectedTeamId = "";
            }
          });
        } catch (e) {
          //
        }
      }
      // tj: logout 托盘停止闪动
      focusSDK.sendIpcMessage("flashSession", { type: "stop" });
      focusSDK.sendIpcMessage("image:removeImagePreviewWindow");
      focusSDK.sendIpcMessage("zoom:logout");
      // 关闭人员选择器弹窗
      closeUserSelectionModal();
      if (service.appRuntime.platform === "WEB") {
        // web端退出登录
        log.debug("web logout ...");
        if (ImService.getInstance()) {
          await ImService.getInstance().logoutIm();
        }
        // 网页端跳转登录的入口
        if (forceLogin()) {
          location.href = getLoginURL();
        }
      } else {
        focusSDK.setAppBadgeCount(0);
        loadAppRuntimeEnv(true, true);
        // 桌面商退出登录
        log.debug("desktop logout ...");
        const netServer = NetworkCheck.getInstance({
          url: getNetCheckUrl()
        });
        netServer?.stopCheck();
        // 有网情况下重新定义连接状态
        if (navigator.onLine) {
          Bus.emit("chat:state", ConnectState.CONNECTING);
        }
        // 退出登录后修改url地址，不刷新页面。 如果不设置，可能会导致上次的url地址拉起其它团队会话的bug
        console.log("logoutpushState", service.appRuntime.registryConfig.auth);
        // if (reason === "logout") {
        //   service?.appRuntime?.registryConfig?.auth &&
        //     delete service.appRuntime.registryConfig.auth;
        //   setCurrentAuthInfo(null);
        // }

        window.history.pushState({}, "", "/");
      }
      Bus.emit("chat:im-logout", "");
    },
    [loadAppRuntimeEnv, service, claerJoyspaceService]
  );
  const onOpenAboutModal = useCallback(() => {
    if (!service) {
      return;
    }
    const config = getAppLogoConfig(service.appRuntime.runtimeConfig);
    const { destroy } = Modals.About({
      t: tRef.current,
      version: getVersionBySdk(focusSDK),
      linkConfig: getServiceAndPrivacyLinks(service.appRuntime),
      ...config,
      onCancel: () => {
        destroy();
      }
    });
  }, [service]);
  /* eslint-disable */
  const navigatorOnline = useCallback(
    debounce(async () => {
      // console.log('网络断开又重连了');
      // 互联网时
      focusSDK.checkUrlOnline({
        type: "changeNetType",
        isPrivateNet: focusSDK.isPrivateNet()
      });
    }, 300),
    [debounce, focusSDK]
  );
  /* eslint-enable */

  useEffect(() => {
    // web端不处理
    if (!isFocusEnv()) {
      return;
    }
    // window.addEventListener('online', navigatorOnline);
    Bus.on("onLineChangeNet", navigatorOnline);
    return () => {
      // window.removeEventListener('online', navigatorOnline);
      Bus.off("onLineChangeNet", navigatorOnline);
    };
  }, [navigatorOnline]);

  const urlOnlineResult = useCallback(({ type, isPrivateNet, publicResult, privateResult }) => {
    console.log(`type: ${type}, publicResult: ${publicResult}, privateResult: ${privateResult}`);
    if (type === "changeNetType") {
      setChangeNetProps({
        newPublicResult: publicResult,
        newPrivateResult: privateResult,
        isPrivateNet
      });
    }
  }, []);

  useEffect(() => {
    // 根据不同系统，可以做样式区分
    document.body.classList.add(focusSDK.getDeviceInfo().platform);
    log.debug("did mount");
    i18n
      .use(initReactI18next)
      .init({
        ns: ["common", "chat", "calendar", "joinMe", "work", "joyspace"],
        resources: resources,
        defaultNS: "common",
        lng: "zh",
        debug: false,
        interpolation: {
          escapeValue: false
        }
      })
      .then((t) => {
        tRef.current = t;
        log.debug("i18n loaded");
        loadAppRuntimeEnv();
      })
      .catch((e) => {
        log.error("failed to load i18n: ", e);
      });
  }, [loadAppRuntimeEnv]);

  useEffect(() => {
    if (service && !commonConfig?.ready) {
      // get comm service
      service?.focusService
        .commonConfig()
        .then((res) => {
          setCommonConfig({
            ...res,
            ready: true
          });
        })
        .catch((e) => {
          setCommonConfig({
            ready: true
          });
        });
    }
  }, [service, commonConfig?.ready]);

  const showNetTable = useCallback(({ fromType = "" }: any = {}) => {
    setFromType(fromType);
    setNetVisible(true);
    const netTableRoot = document.querySelector(".net-table-modal")?.parentNode;
    if (netTableRoot) {
      (netTableRoot as HTMLElement).removeAttribute("style");
    }
    // focusSDK.checkUrlOnline({
    //     type: 'netSetting',
    //     isPrivateNet: focusSDK.isPrivateNet(),
    // });
  }, []);

  const hideNetTable = useCallback(() => {
    setNetVisible(false);
  }, []);

  const onUpdateAuthInfo = (authInfo: any) => {
    setCurrentAuthInfo(authInfo);
  };

  const netSettingResult = useCallback(({ type, publicResult, privateResult }) => {
    console.log(
      `netSettingResult: type, ${type}, publicResult, ${publicResult}, privateResult, ${privateResult}`
    );
    if (type === "netSetting") {
      setLoginCurrentNetInfo({
        loginPrivateResult: privateResult,
        loginPublicResult: publicResult
      });
    }
  }, []);
  useEffect(() => {
    try {
      let config = JSON.parse(focusSDK.getConfigData());
      const { settings } = config;
      const { netSettingChanged, egovFirst } = settings || {};
      console.log("netSettingChanged", netSettingChanged);
      if (netSettingChanged) {
        let info = `由于${
          egovFirst ? "政务外网" : "互联网"
        }异常，无法完成切换，请您重试或联系所在单位网络管理员解决。`;
        let changeSuccess = false;
        if (egovFirst && focusSDK?.isPrivateNet()) {
          info = "已切换至政务外网";
          changeSuccess = true;
        }
        if (!egovFirst && !focusSDK?.isPrivateNet()) {
          info = "已切换至互联网";
          changeSuccess = true;
        }
        sentryManual.captureMessage(info, "net-change-success");
        if (changeSuccess) {
          message.info(info);
        } else {
          message.warning(info, 5);
        }
        focusSDK.setConfigData("settings|netSettingChanged", false);
      }
    } catch (e) {
      focusSDK.printLog("getConfigData--->>>", e);
      console.log("getConfigData--->>>", e);
    }
  }, []);
  useEffect(() => {
    focusSDK.off("configDataChange", onConfigDataChange).on("configDataChange", onConfigDataChange);
    focusSDK.off("loginSuccess", onLoginSuccess).on("loginSuccess", onLoginSuccess);

    focusSDK.off("app:openAboutModal", onOpenAboutModal).on("app:openAboutModal", onOpenAboutModal);

    focusSDK.on("urlOnline:reply", urlOnlineResult);
    focusSDK.on("showNetTable", showNetTable);
    focusSDK.on("hideNetTable", hideNetTable);
    // 点击网络检测时更新当前网络检测值
    focusSDK.on("netSettingResult:reply", netSettingResult);

    return () => {
      focusSDK.off("configDataChange", onConfigDataChange);
      focusSDK.off("loginSuccess", onLoginSuccess);

      focusSDK.off("app:openAboutModal", onOpenAboutModal);
      focusSDK.off("urlOnline:reply", urlOnlineResult);
      focusSDK.off("showNetTable", showNetTable);
      focusSDK.off("hideNetTable", hideNetTable);
    };
  }, [
    onConfigDataChange,
    onLoginSuccess,
    onOpenAboutModal,
    urlOnlineResult,
    showNetTable,
    hideNetTable,
    netSettingResult
  ]);

  useEffect(() => {
    focusSDK.off("logout", onLogout).on("logout", onLogout);
    return () => {
      focusSDK.off("logout", onLogout);
    };
  }, [onLogout]);
  const onlineFn = useCallback(() => {
    console.log("触发online--");
    const netServer = NetworkCheck.getInstance({
      url: getNetCheckUrl()
    });
    netServer.stopCheck();
    setCurrentNetOffline(false);
  }, []);
  const offlineFn = useCallback(() => {
    console.log("触发offline--");
    const netServer = NetworkCheck.getInstance({
      url: getNetCheckUrl()
    });
    if (!netServer.isStartCheck()) {
      netServer.startCheck();
    }
    focusSDK.printLog("offlineFn中---触发offline---");
    setCurrentNetOffline(true);
  }, []);
  useEffect(() => {
    if (!service) {
      return;
    }
    if (
      service.appRuntime.platform !== "WEB" &&
      !service.appRuntime.registryConfig.auth &&
      pageName !== "builtin-browser"
    ) {
      // 登录页通过http检测
      const netServer = NetworkCheck.getInstance({
        url: getNetCheckUrl()
      });
      // 登录页不开启定时器检测
      // if (!netServer.isStartCheck()) {
      //     netServer.startCheck();
      // }
      netServer.off("online", onlineFn);
      netServer.off("offline", offlineFn);
      netServer.on("online", onlineFn);
      netServer.on("offline", offlineFn);
      netServer.checkState().then((flag) => {
        if (!flag) {
          netServer.emit("offline");
        }
      });
      return () => {
        netServer.off("online", onlineFn);
        netServer.off("offline", offlineFn);
      };
    }
  }, [onlineFn, offlineFn, service, pageName]);
  let comp;
  if (!service) {
    comp = (
      <div className="flex1 flex-center">
        <StartLoading />
      </div>
    );
  } else if (
    forceLogin() &&
    service.appRuntime.platform !== "WEB" &&
    !service.appRuntime.registryConfig.auth &&
    pageName !== "builtin-browser"
  ) {
    // 桌面端跳转到登录页
    // if (service.appRuntime.platform === 'WEB') {
    //     location.href = service.appRuntime.loginEntry();
    // }
    comp = (
      <GlobalContext.Provider
        value={{
          focusSDK,
          service,
          appRuntimeEnv: service.appRuntime,
          commonConfig
        }}
      >
        <LoginPage />
      </GlobalContext.Provider>
    );
  } else if (REACT_APP_BUILD_ELECTRON === "true") {
    comp = (
      <HashRouter basename={service.appRuntime.webConfig.routeBase}>
        <AppContainer
          setCurrentNetOffline={setCurrentNetOffline}
          service={service}
          onUpdateAuthInfo={onUpdateAuthInfo}
          onGlobalError={onGlobalError}
          localConfigData={localConfigData}
          commonConfig={commonConfig}
        />
      </HashRouter>
    );
  } else {
    comp = (
      <BrowserRouter
        basename={
          getWebEnvObj().REACT_APP_ROUTE_BASE || service.appRuntime.webConfig.routeBase || "/client"
        }
      >
        <AppContainer
          setCurrentNetOffline={setCurrentNetOffline}
          service={service}
          onUpdateAuthInfo={onUpdateAuthInfo}
          onGlobalError={onGlobalError}
          localConfigData={localConfigData}
          commonConfig={commonConfig}
        />
      </BrowserRouter>
    );
  }
  return (
    <>
      {isHomePage && currentAuthInfo && (
        <UpgradeModal
          focusSDK={focusSDK}
          appRuntimeEnv={service?.appRuntime}
          upgradeVersionInfo={service?.appRuntime.runtimeConfig.config.upgradeVersionInfo || []}
        />
      )}
      {/* <ChangeNetModal {...changeNetProps} /> */}
      <NetTable
        visible={netVisible}
        appRuntimeEnv={service?.appRuntime}
        authInfo={currentAuthInfo}
        fromType={fromType}
        loginCurrentNetInfo={loginCurrentNetInfo}
        localConfigData={localConfigData}
      />
      {isHomePage && (
        <OfflineTip
          appRuntimeEnv={service?.appRuntime}
          currentNetOffline={currentNetOffline}
          localConfigData={localConfigData}
        />
      )}
      {comp}
    </>
  );
}

export default App;
